import React, { useState } from"react";

import { Badge, Modal, Spinner } from"react-bootstrap";

import TenantAvatar from"./TenantAvatar";

import { useProfileGetMyAcesses } from"api/generated/endpoints/profile/profile";
import useAuth from"global/Auth/use";

import type { UserAcesseItem, RelationData } from"../index";

const RELATION_LABELS: Record<number, string> = {
    0:"Proprietário",
    1:"Funcionário",
    2:"Cliente",
    3:"Declarante",
};

const RELATION_VARIANTS: Record<number, string> = {
    0:"primary",
    1:"success",
    2:"warning",
    3:"info",
};

const BsBuilding = ({ className ="", size }: { className?: string; size?: number } = {}) => (
    <i className={`fa-solid fa-building ${className}`.trim()} style={size ? { fontSize: size } : undefined} />
);

const BsBoxArrowInRight = ({ className ="", size }: { className?: string; size?: number } = {}) => (
    <i className={`fa-solid fa-right-to-bracket ${className}`.trim()} style={size ? { fontSize: size } : undefined} />
);

const List: React.FC<{
    relationData: RelationData;
    onHide: () => void
}> = ({ relationData, onHide }) => {

    const { data, isLoading: loading, isError } = useProfileGetMyAcesses();
    const list: UserAcesseItem[] = data ?? [];
    const [switching, setSwitching] = useState<string | null>(null);
    const error = isError ? "Não foi possível carregar os acessos." : null;

    const switchTenant = useAuth((s) => s.switchTenant);

    const handleSwitch = async (tenantId: number, relationType: number) => {
        const key = `${tenantId}-${relationType}`;
        setSwitching(key);
        const ok = await switchTenant(tenantId, relationType as 0 | 1 | 2 | 3);
        setSwitching(null);
        if (ok) onHide();
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted">Carregando acessos…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-danger py-5">
                <div>{error}</div>
            </div>
        );
    }

    if (!list.length) {
        return (
            <div className="text-center text-muted py-5">
                <BsBuilding size={32} className="mb-2 opacity-50" />
                <div>Nenhum acesso encontrado.</div>
            </div>
        );
    }

    return (
        <>
            <StyleComponent />

            <div className="d-flex flex-column gap-3">
                {list.map((item) => (
                    <div key={item.tenant.id} className="ac-group">
                        <div className="ac-group-header">
                            <TenantAvatar name={item.tenant.companyName} url={item.tenant.avatarUrl} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="ac-tenant-name">{item.tenant.companyName}</div>
                                <div className="ac-cnpj">{item.tenant.cnpj}</div>
                            </div>
                        </div>

                        {item.relationData.map((rel) => {
                            const key = `${item.tenant.id}-${rel.relationType}`;
                            const busy = switching === key;
                            return (
                                <div key={rel.relationId} className="ac-role-row">
                                    <Badge
                                        bg={RELATION_VARIANTS[rel.relationType] ??"secondary"}
                                        style={{ fontSize:".75rem", fontWeight: 600, padding:"5px 10px" }}
                                    >
                                        {RELATION_LABELS[rel.relationType] ?? `Tipo ${rel.relationType}`}
                                    </Badge>
                                    <button
                                        className="ac-enter-btn"
                                        disabled={switching !== null}
                                        onClick={() => handleSwitch(item.tenant.id, rel.relationType)}
                                    >
                                        {busy ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <BsBoxArrowInRight size={14} />
                                        )}
                                        Entrar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </>
    );
};

const StyleComponent: React.FC = () => {
    return (
        <style>{`
            .ac-group {
                border: 1px solid rgba(0,0,0,.09);
                border-radius: 16px;
                overflow: hidden;
                background: #fff;
            }
            .ac-group-header {
                display: flex;
                align-items: center;
                gap: 14px;
                padding: 14px 16px;
                background: rgba(13,110,253,.04);
                border-bottom: 1px solid rgba(0,0,0,.07);
            }
            .ac-avatar {
                width: 44px;
                height: 44px;
                border-radius: 10px;
                object-fit: cover;
                flex-shrink: 0;
                border: 1px solid rgba(0,0,0,.08);
            }
            .ac-avatar-initials {
                width: 44px;
                height: 44px;
                border-radius: 10px;
                background: linear-gradient(135deg, #0d6efd22, #0d6efd44);
                border: 1px solid rgba(13,110,253,.18);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: .82rem;
                color: #0d6efd;
                flex-shrink: 0;
            }
            .ac-tenant-name {
                font-weight: 700;
                font-size: .95rem;
                color: rgba(33,37,41,.92);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .ac-cnpj {
                font-size: .72rem;
                color: rgba(33,37,41,.45);
                letter-spacing: .03em;
                margin-top: 1px;
            }
            .ac-role-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 10px 16px;
                gap: 12px;
                transition: background .12s ease;
            }
            .ac-role-row:not(:last-child) {
                border-bottom: 1px solid rgba(0,0,0,.05);
            }
            .ac-role-row:hover { background: rgba(0,0,0,.02); }
            .ac-enter-btn {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 5px 13px;
                font-size: .8rem;
                font-weight: 600;
                border-radius: 10px;
                border: 1px solid rgba(13,110,253,.3);
                background: rgba(13,110,253,.06);
                color: #0d6efd;
                cursor: pointer;
                white-space: nowrap;
                transition: background .12s ease, border-color .12s ease;
                flex-shrink: 0;
            }
            .ac-enter-btn:hover { background: rgba(13,110,253,.12); border-color: rgba(13,110,253,.5); }
            .ac-enter-btn:disabled { opacity: .5; pointer-events: none; }
        `}</style>
    )
}

export default List;
